import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';
import { usePlayAlongIdlePause } from './usePlayAlongIdlePause';

export interface PlayAlongIdlePauseProps {
  /** True while the surface is playing. */
  playing: boolean;
  /** Pause the surface, as its own pause control does. */
  pause: () => void;
  /** Start the surface again, as its own play control does. */
  resume: () => void;
  timeoutMs?: number;
}

/**
 * Ungraded play pauses after 30 minutes with no interaction, so a session left
 * running in a forgotten tab stops asking for phrases, and asks "Still
 * playing?". A tap on the prompt starts play again. Graded play is not
 * affected: it never arms.
 */
export default function PlayAlongIdlePause({
  playing,
  pause,
  resume,
  timeoutMs,
}: PlayAlongIdlePauseProps) {
  const graded = useEffectiveGrading();
  const [asking, setAsking] = useState(false);

  usePlayAlongIdlePause({
    active: !graded && playing,
    onIdle: () => {
      pause();
      setAsking(true);
    },
    timeoutMs,
  });

  // Started again some other way (the play button, the space bar): the
  // question has been answered.
  useEffect(() => {
    if (playing) setAsking(false);
  }, [playing]);

  if (!asking) return null;
  return (
    <Box
      component="button"
      type="button"
      data-testid="play-along-idle-prompt"
      onClick={() => {
        setAsking(false);
        resume();
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 3500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        border: 'none',
        padding: '24px',
        background: 'rgba(7, 20, 35, 0.75)',
        color: '#FFFFFF',
        fontFamily: 'Lato',
        fontSize: '24px',
        textAlign: 'center',
        cursor: 'pointer',
      }}
    >
      Still playing? Tap to keep going.
    </Box>
  );
}
